/**
 * "What happened just before this error" — a small trail attached to
 * every error log (see buildBaseErrorLogPayload). An error that carries no
 * detail of its own (e.g. a promise rejected with `{}` by a blocked third-party
 * script) can usually be traced from what it sits next to: the script that
 * failed to load a few ms earlier, the request that just failed, the route the
 * user was on, the button they clicked.
 *
 * Persisted in sessionStorage (per browser tab) so it survives full page
 * loads — many transitions in this app are hard page loads (login redirects,
 * `window.location.assign/replace`, the not-found redirect), and an in-memory
 * trail was wiped by each one, leaving only the last step or two.
 *
 * Kept deliberately privacy-safe: URLs are reduced to origin + path (no query
 * string or hash, which can carry eurl tokens). Clicks record the element's
 * tag / id / classes / data-testid plus a short label (its visible text,
 * aria-label or <label>, cut to 15 characters). Form field changes record the
 * field's label and, for a dropdown, the chosen option — but for a text field
 * only how many characters were typed, never the text itself, unless the field
 * opts in with a `data-breadcrumb-value` attribute. Password fields never
 * record a length or value.
 */

export type ErrorLogBreadcrumbKind =
  | 'page-load'
  | 'navigation'
  | 'click'
  | 'change'
  | 'submit'
  | 'resource-failed'
  | 'request-failed';

export interface ErrorLogBreadcrumb {
  kind: ErrorLogBreadcrumbKind;
  /** Route path, failed resource/request URL, or clicked element descriptor.
   * For `page-load`: the path plus how the page was loaded (navigate / reload / back_forward). */
  detail: string;
  /** Tag of the element that failed to load (`resource-failed` only). */
  tag?: string;
  /** What the clicked element says (`click` only): its visible text, aria-label,
   * title or associated <label>, first 15 characters. Never a typed value. */
  label?: string;
  /** New state of a clicked checkbox / radio / switch (`click` only). */
  checked?: boolean;
  /** What changed (`change` only): the chosen option of a dropdown, or for a
   * text field "(typed N chars)" / "(cleared)" — the actual text only when the
   * field has `data-breadcrumb-value`. First 15 characters. */
  value?: string;
  /** HTTP status (`request-failed` only; 0 = no response). */
  status?: number;
  /** When this happened (ISO time). */
  at: string;
  /** How many milliseconds before the logged error this happened. */
  msBeforeError?: number;
}

const MAX_BREADCRUMBS = 20;
/** Steps older than this aren't "just before the error" — e.g. a tab left open
 * overnight would otherwise carry yesterday's clicks into today's error. */
const MAX_BREADCRUMB_AGE_MS = 30 * 60 * 1000;
const STORAGE_KEY = 'errorLog:breadcrumbs';
const MAX_DETAIL_LENGTH = 200;

function loadStoredBreadcrumbs(): Array<ErrorLogBreadcrumb> {
  try {
    const raw = globalThis.sessionStorage?.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? (parsed as Array<ErrorLogBreadcrumb>).slice(-MAX_BREADCRUMBS)
      : [];
  } catch {
    return [];
  }
}

let breadcrumbs: Array<ErrorLogBreadcrumb> = loadStoredBreadcrumbs();
let isInitialized = false;

/** Best-effort — sessionStorage can be full or blocked; the trail then just lives in memory. */
function persistBreadcrumbs(): void {
  try {
    globalThis.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(breadcrumbs));
  } catch {
    // Not fatal.
  }
}

/** Origin + path only — drops the query string and hash. */
export function toSafeUrl(value: string): string {
  try {
    const url = new URL(value, globalThis.location?.href);
    return `${url.origin}${url.pathname}`.slice(0, MAX_DETAIL_LENGTH);
  } catch {
    return value.split(/[#?]/)[0]?.slice(0, MAX_DETAIL_LENGTH) ?? '';
  }
}

export function recordBreadcrumb(
  breadcrumb: Omit<ErrorLogBreadcrumb, 'at' | 'msBeforeError'>
): void {
  const last = breadcrumbs.at(-1);
  // A re-render / replaceState to the same path isn't a new step. Only
  // navigation is collapsed — clicking the same button twice is two actions.
  if (
    breadcrumb.kind === 'navigation' &&
    last &&
    last.kind === breadcrumb.kind &&
    last.detail === breadcrumb.detail &&
    last.status === breadcrumb.status
  ) {
    return;
  }
  breadcrumbs.push({
    ...breadcrumb,
    detail: breadcrumb.detail.slice(0, MAX_DETAIL_LENGTH),
    at: new Date().toISOString(),
  });
  if (breadcrumbs.length > MAX_BREADCRUMBS) {
    breadcrumbs = breadcrumbs.slice(-MAX_BREADCRUMBS);
  }
  persistBreadcrumbs();
}

/** Snapshot for an error payload, each entry stamped with how long before now it happened. */
export function getBreadcrumbs(): Array<ErrorLogBreadcrumb> {
  const now = Date.now();
  return breadcrumbs
    .map((breadcrumb) => ({
      ...breadcrumb,
      msBeforeError: Math.max(0, now - Date.parse(breadcrumb.at)),
    }))
    .filter((breadcrumb) => breadcrumb.msBeforeError <= MAX_BREADCRUMB_AGE_MS);
}

/** Test-only reset. */
export function clearBreadcrumbs(): void {
  breadcrumbs = [];
  persistBreadcrumbs();
}

type DescribableElement = Element & {
  id?: string;
  className?: unknown;
  dataset?: DOMStringMap;
};

export function describeElement(element: DescribableElement): string {
  const classes =
    typeof element.className === 'string'
      ? element.className.trim().split(/\s+/).filter(Boolean).slice(0, 2)
      : [];
  const testId = element.dataset?.testid;
  return [
    element.tagName,
    element.id ? `#${element.id}` : '',
    ...classes.map((name) => `.${name}`),
    testId ? `[data-testid="${testId}"]` : '',
  ].join('');
}

const INTERACTIVE_SELECTOR =
  'a,button,input,select,textarea,label,summary,[role="button"],[role="link"],[role="tab"],[role="checkbox"],[role="switch"],[role="radio"],[role="option"]';

const MAX_LABEL_LENGTH = 15;

/** Collapses whitespace and keeps the first 15 characters (plus "…"). */
export function toShortLabel(value: string | null | undefined): string | undefined {
  const text = (value ?? '').replaceAll(/\s+/g, ' ').trim();
  if (!text) {
    return undefined;
  }
  return text.length > MAX_LABEL_LENGTH ? `${text.slice(0, MAX_LABEL_LENGTH)}…` : text;
}

/** The first value that has visible text (skips null, undefined and blank strings). */
function firstNonBlank(...values: Array<string | null | undefined>): string | undefined {
  return values.find((value) => typeof value === 'string' && value.trim() !== '') ?? undefined;
}

/** Input types whose `value` is what the user typed — never recorded. */
const BUTTON_INPUT_TYPES = new Set(['button', 'submit', 'reset']);

function getAssociatedLabelText(
  element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
): string | undefined {
  return firstNonBlank(
    element.labels?.[0]?.textContent,
    element.closest('label')?.textContent
  );
}

/** What the user would say they clicked: "Export to Exc…", "UK" (checkbox)… */
export function getElementLabel(element: Element): string | undefined {
  const ariaLabel = element.getAttribute('aria-label');
  if (
    element instanceof HTMLInputElement ||
    element instanceof HTMLSelectElement ||
    element instanceof HTMLTextAreaElement
  ) {
    if (element instanceof HTMLInputElement && BUTTON_INPUT_TYPES.has(element.type)) {
      return toShortLabel(firstNonBlank(element.value, ariaLabel));
    }
    // Text fields, checkboxes, selects: describe the field, never its content.
    return toShortLabel(
      firstNonBlank(
        ariaLabel,
        getAssociatedLabelText(element),
        element.getAttribute('placeholder'),
        element.getAttribute('name')
      )
    );
  }
  return toShortLabel(
    firstNonBlank(
      ariaLabel,
      element.getAttribute('title'),
      element.textContent,
      element.querySelector('img')?.getAttribute('alt')
    )
  );
}

function getCheckedState(element: Element): boolean | undefined {
  if (
    element instanceof HTMLInputElement &&
    (element.type === 'checkbox' || element.type === 'radio')
  ) {
    return element.checked;
  }
  const ariaChecked = element.getAttribute('aria-checked');
  if (ariaChecked === 'true' || ariaChecked === 'false') {
    // The click handler runs before the component flips aria-checked, so the
    // new state is the opposite of what the attribute says right now.
    return ariaChecked !== 'true';
  }
  return undefined;
}

/** Opt-in marker: a field whose actual value is safe to record (e.g. a report search box). */
const VALUE_OPT_IN_ATTRIBUTE = 'data-breadcrumb-value';

function describeChangedValue(
  element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
): string | undefined {
  if (element instanceof HTMLSelectElement) {
    const chosen = Array.from(element.selectedOptions, (option) =>
      option.text.trim()
    );
    return chosen.length > 0 ? toShortLabel(chosen.join(', ')) : '(none)';
  }
  if (element.type === 'password') {
    return element.value ? '(typed)' : '(cleared)';
  }
  if (!element.value) {
    return '(cleared)';
  }
  if (element.hasAttribute(VALUE_OPT_IN_ATTRIBUTE)) {
    return toShortLabel(element.value);
  }
  return `(typed ${element.value.length} chars)`;
}

/** `change` fires once when a dropdown selection is made or a text field is
 * left after editing — not per keystroke — so the trail doesn't fill up. */
function handleChange(event: Event): void {
  const target = event.target;
  if (
    !(
      target instanceof HTMLInputElement ||
      target instanceof HTMLSelectElement ||
      target instanceof HTMLTextAreaElement
    )
  ) {
    return;
  }
  // Checkbox / radio state is already on the click breadcrumb; file inputs
  // carry file names, which aren't needed here.
  if (
    target instanceof HTMLInputElement &&
    (target.type === 'checkbox' || target.type === 'radio' || target.type === 'file')
  ) {
    return;
  }
  const label = getElementLabel(target);
  recordBreadcrumb({
    kind: 'change',
    detail: describeElement(target),
    ...(label ? { label } : {}),
    value: describeChangedValue(target),
  });
}

/** Form submits — including pressing Enter in a field, which fires no click. */
function handleSubmit(event: Event): void {
  const target = event.target;
  if (!(target instanceof HTMLFormElement)) {
    return;
  }
  const label = toShortLabel(
    firstNonBlank(target.getAttribute('aria-label'), target.getAttribute('name'))
  );
  recordBreadcrumb({
    kind: 'submit',
    detail: describeElement(target),
    ...(label ? { label } : {}),
  });
}

function handleClick(event: MouseEvent): void {
  const target = event.target;
  if (!(target instanceof Element)) {
    return;
  }
  const element = target.closest(INTERACTIVE_SELECTOR) ?? target;
  // Clicking a <label> fires a second, synthetic click on its checkbox/input —
  // record that one (it knows the new checked state) instead of both.
  if (element instanceof HTMLLabelElement && element.control) {
    return;
  }
  const label = getElementLabel(element);
  const checked = getCheckedState(element);
  recordBreadcrumb({
    kind: 'click',
    detail: describeElement(element),
    ...(label ? { label } : {}),
    ...(checked === undefined ? {} : { checked }),
  });
}

/** `<script>`/`<img>`/`<link>` load failures don't bubble, but they do pass
 * through the capture phase on window — the only place they can be observed. */
function handleResourceError(event: Event): void {
  const target = event.target;
  if (!(target instanceof Element)) {
    return;
  }
  const url =
    (target as HTMLScriptElement | HTMLImageElement).src ||
    (target as HTMLLinkElement).href;
  if (!url) {
    return;
  }
  recordBreadcrumb({
    kind: 'resource-failed',
    tag: target.tagName,
    detail: toSafeUrl(url),
  });
}

function recordNavigation(): void {
  const path = globalThis.location?.pathname;
  if (path) {
    recordBreadcrumb({ kind: 'navigation', detail: path });
  }
}

function patchHistoryMethod(method: 'pushState' | 'replaceState'): void {
  const { history } = globalThis;
  if (!history) {
    return;
  }
  // Bound once here so the original always runs against `history` itself.
  const original = history[method].bind(history);
  history[method] = (...historyArguments: Parameters<History['pushState']>): void => {
    original(...historyArguments);
    recordNavigation();
  };
}

/** Failed same-origin (or Timing-Allow-Origin) requests, including ones not made
 * through axios. Cross-origin requests without that header report status 0 for
 * every request, so only real HTTP error statuses are recorded. */
function observeFailedRequests(): void {
  if (typeof PerformanceObserver === 'undefined') {
    return;
  }
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const status = (
          entry as PerformanceResourceTiming & { responseStatus?: number }
        ).responseStatus;
        if (typeof status === 'number' && status >= 400) {
          recordBreadcrumb({
            kind: 'request-failed',
            detail: toSafeUrl(entry.name),
            status,
          });
        }
      }
    });
    observer.observe({ type: 'resource', buffered: true });
  } catch {
    // Unsupported entry type in this browser — breadcrumbs just lack this source.
  }
}

/** Installs the listeners once. Safe to call repeatedly. */
export function initializeBreadcrumbs(): void {
  if (isInitialized || typeof globalThis.addEventListener !== 'function') {
    return;
  }
  isInitialized = true;

  // Marks each full page load, so a hard navigation or reload is visible in the
  // trail instead of silently splitting it.
  const navigationEntry = globalThis.performance?.getEntriesByType?.('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  recordBreadcrumb({
    kind: 'page-load',
    detail: `${globalThis.location?.pathname ?? ''} (${navigationEntry?.type ?? 'navigate'})`,
  });
  globalThis.addEventListener('popstate', recordNavigation);
  patchHistoryMethod('pushState');
  patchHistoryMethod('replaceState');
  globalThis.addEventListener('click', handleClick, true);
  globalThis.addEventListener('change', handleChange, true);
  globalThis.addEventListener('submit', handleSubmit, true);
  globalThis.addEventListener('error', handleResourceError, true);
  observeFailedRequests();
}
